import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import Select from "../../common/components/form/select";
import { getSpotByTermSchedule } from "../../common/services/markets/termService";
import { pivotQualityAttributes } from "../../common/services/products/productUtilities";
import { SHA256 } from "crypto-js";
import { joinArrays } from "../../common/services/utilities";
import { aggregateMarks } from "../../common/services/marketdata/marketDataUtilities";
import ThemeableAgGrid from "../../common/components/agGrid/ThemeableAgGrid";

const AttributeSpreads = ({
  product,
  marks,
  fromDate,
  toDate,
  termScheduleId,
  currencySymbol,
  pricePrecision,
}) => {
  const { t } = useTranslation(["marketdata"]);
  const [qualityAttributes, setQualityAttributes] = useState([]);
  const [qualityAttributeId, setQualityAttributeId] = useState(
    product.qualityAttributes[0]?.qualityAttributeId
  );
  const [rowData, setRowData] = useState([]);

  // Load quality attributes and marks on mount or when dependencies change
  useEffect(() => {
    let isMounted = true;
    async function fetchData() {
      const filteredAttributes = product.qualityAttributes.filter(
        (x) => x.isPriceDriver
      );
      const selectedId = filteredAttributes[0]?.qualityAttributeId;
      const terms = await getSpotByTermSchedule(
        termScheduleId,
        fromDate.toLocaleDateString("fr-CA"),
        toDate.toLocaleDateString("fr-CA")
      );
      const spotTerms = terms.map((x) => x.termId + x.marketDate);
      const spotMarks = marks.filter((x) =>
        spotTerms.includes(x.termId + x.marketDate)
      );
      const aggregatedMarks = aggregateMarks(spotMarks);
      if (!isMounted) return;
      setQualityAttributes(filteredAttributes);
      setQualityAttributeId(selectedId);
      // Prepare table data for the default attribute
      setRowData(prepareTableData(selectedId, aggregatedMarks));
    }
    fetchData();
    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product, marks, fromDate, toDate, termScheduleId]);

  // Update table data when qualityAttributeId changes
  useEffect(() => {
    if (!qualityAttributeId || !marks.length) return;
    // const filteredAttributes = product.qualityAttributes.filter(
    //   (x) => x.isPriceDriver
    // );
    const terms = marks; // Already filtered in fetchData
    setRowData(prepareTableData(qualityAttributeId, terms));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qualityAttributeId]);

  // Helper to prepare AG Grid row data
  function prepareTableData(qualityAttributeId, marksInput) {
    const { qualityAttributeValues /*, qualityAttributes*/ } = product;
    // const qualityAttribute = qualityAttributes.find(
    //   (x) => x.qualityAttributeId === qualityAttributeId
    // );
    const pivotedAttributes = pivotQualityAttributes(product);
    const attributeValuesWithHashes = pivotedAttributes.map((obj) => {
      const sortedQualityAttributeValues = obj.qualityAttributeValues
        .map(
          (qualityAttributeValue) =>
            qualityAttributeValue.qualityAttributeValueId
        )
        .filter((value) => value !== undefined)
        .sort();
      const concatenatedValues = sortedQualityAttributeValues.join("");
      const qualityAttributeValueHash = SHA256(concatenatedValues)
        .toString()
        .toUpperCase();
      const qualityAttributeValue = obj[qualityAttributeId];
      const qualityAttributeValueFull = obj.qualityAttributeValues.find(
        (x) => x.qualityAttributeId === qualityAttributeId
      );

      const qualityAttributeValueId =
        qualityAttributeValueFull?.qualityAttributeValueId;
      return {
        qualityAttributeValue,
        qualityAttributeValueId,
        qualityAttributeValueHash,
      };
    });
    const marksWithAttributeValues = joinArrays(
      marksInput,
      attributeValuesWithHashes,
      "qualityAttributeValueHash"
    );
    const data = marksWithAttributeValues.reduce((acc, curr) => {
      if (
        !acc[curr.qualityAttributeValueId] ||
        new Date(curr.marketDate) >
          new Date(acc[curr.qualityAttributeValueId].marketDate)
      ) {
        acc[curr.qualityAttributeValueId] = curr;
      }
      return acc;
    }, {});
    const qualityAttributeValuesFiltered = qualityAttributeValues
      .filter((x) => x.qualityAttributeId === qualityAttributeId)
      .map((x) => x.qualityAttributeValueId);
    // Build matrixed row data for AG Grid
    return qualityAttributeValuesFiltered.map((qualityAttributeValueId) => {
      const row = {
        qualityAttributeValue: qualityAttributeValues.find(
          (x) => x.qualityAttributeValueId === qualityAttributeValueId
        ).value,
        id: qualityAttributeValueId, // For getRowId
      };
      qualityAttributeValuesFiltered.forEach((otherQualityAttributeValueId) => {
        const record = data[qualityAttributeValueId];
        const otherRecord = data[otherQualityAttributeValueId];
        let priceDifference;
        if (qualityAttributeValueId === otherQualityAttributeValueId) {
          priceDifference = NaN;
        } else if (
          otherRecord &&
          record &&
          typeof otherRecord.price === "number" &&
          typeof record.price === "number"
        ) {
          priceDifference = otherRecord.price - record.price;
        } else {
          priceDifference = null;
        }
        row[otherQualityAttributeValueId] = priceDifference;
      });
      return row;
    });
  }

  // Memoize AG Grid column definitions
  const columnDefs = useMemo(() => {
    if (!qualityAttributeId || !product) return [];
    const { qualityAttributeValues, qualityAttributes } = product;
    const qualityAttribute = qualityAttributes.find(
      (x) => x.qualityAttributeId === qualityAttributeId
    );
    // First column: row label
    const columns = [
      {
        field: "qualityAttributeValue",
        headerName: qualityAttribute
          ? t(qualityAttribute.qualityAttributeName)
          : t("QualityAttributeValue"),
        pinned: "left",
        cellClass: "table-bold",
        suppressMovable: true,
        sortable: false,
        filter: false,
      },
    ];
    // Matrix columns
    qualityAttributeValues
      .filter((x) => x.qualityAttributeId === qualityAttributeId)
      .forEach((attributeValue) => {
        columns.push({
          field: attributeValue.qualityAttributeValueId,
          headerName: attributeValue.value,
          flex: 1,
          cellRenderer: (params) => {
            const val = params.value;
            let color = "";
            if (val < 0) color = "var(--theme-red)";
            else if (val > 0) color = "var(--theme-green)";
            return !isNaN(parseFloat(val)) ? (
              <span style={{ color }}>
                {currencySymbol + val.toFixed(pricePrecision)}
              </span>
            ) : null;
          },
          sortable: false,
          filter: false,
        });
      });
    return columns;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qualityAttributeId, product, t, currencySymbol, pricePrecision]);

  // AG Grid getRowId
  const getRowId = useCallback((params) => params.data.id?.toString(), []);

  // Handle select change
  const handleSelectChange = (e) => {
    setQualityAttributeId(e.currentTarget.value);
  };

  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col-md-12">
          <Select
            idField="qualityAttributeId"
            name="qualityAttribute"
            optionField="qualityAttributeName"
            disabled={qualityAttributes.length < 2}
            value={qualityAttributeId}
            label={t("QualityAttribute")}
            options={qualityAttributes}
            onChange={handleSelectChange}
          />
        </div>
      </div>
      <div className="row">
        <div className="col-md-12">
          <ThemeableAgGrid
            className="table-condensed"
            columnDefs={columnDefs}
            rowData={rowData}
            getRowId={getRowId}
            allowHorizontalScroll={false}
            animateRows={true}
            domLayout="autoHeight"
          />
        </div>
      </div>
    </div>
  );
};

export default AttributeSpreads;
